/**
 * "That's wrong" — a user's correction of the RFQ classifier, held for the session.
 *
 * ---- Why an override exists at all ----
 *
 * `classifyRfqMail` is a weighted signal table, not a model and not a calibrated probability (see
 * `mailClassify.ts`'s header). It will be wrong. A real example: `ar9f1eknij`, a supplier's
 * quotation, scored "RFQ? 55%" and offered to create an enquiry from it. A heuristic a user cannot
 * correct trains them to distrust the badge everywhere, including where it is right.
 *
 * ---- Why in memory, and what it costs ----
 *
 * The correction SHOULD live on the `Communication`, so it survives a reload and everyone sees it.
 * Frappe's `_user_tags` is on the wire and is the natural home. Measured 2026-08-08, we cannot
 * write it:
 *
 * ```
 * POST frappe.desk.doctype.tag.tag.add_tag   → PermissionError
 * POST frappe.client.set_value               → PermissionError
 * PUT  /api/resource/Communication/<name>    → PermissionError
 *   "User rfq@gmail.com does not have doctype access via role permission for document Communication"
 * ```
 *
 * Reads are fine; writes are refused at DOCTYPE level, so it is not the tag API specifically. Until
 * the Frappe team grants write permission for the app role or exposes a whitelisted setter (filed),
 * the honest options were "in memory" or "nothing", and nothing is worse. `CLAUDE.md` forbids
 * `localStorage`/`sessionStorage`, so this is a module-level map, exactly as `extractionStore.ts`
 * holds extraction runs.
 *
 * **So a correction is lost on reload, and is not shared with colleagues.** The UI says so rather
 * than implying it stuck. When the backend lands, only `setOverride` changes.
 */

import { isSupplierReply, type RfqVerdict } from './mailClassify'
import type { MailMessage } from './types'

/** What a user can say about a mail the classifier got wrong. */
export type MailVerdictOverride = 'rfq' | 'not-rfq' | 'supplier-reply'

const overrides = new Map<string, MailVerdictOverride>()
const listeners = new Set<() => void>()

const emit = () => {
  for (const listener of listeners) listener()
}

/** `useSyncExternalStore` subscription — see `useMailVerdictOverride`. */
export function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getOverride(mailId: string): MailVerdictOverride | null {
  return overrides.get(mailId) ?? null
}

/** Passing `null` clears the correction and restores the classifier's own verdict. */
export function setOverride(mailId: string, verdict: MailVerdictOverride | null): void {
  if (verdict === null) overrides.delete(mailId)
  else overrides.set(mailId, verdict)
  emit()
}

/** Test seam only — never called by the app. */
export function __resetOverrides(): void {
  overrides.clear()
  emit()
}

/**
 * The verdict to SHOW: the user's correction where they gave one, else the classifier's.
 *
 * `supplier-reply` collapses to `not-rfq` for the badge — a supplier's quotation is genuinely not
 * an incoming RFQ — while staying distinct in `getOverride`, because it also suppresses the
 * "Create enquiry from this mail" action and the reply-detection path reads it.
 */
export function effectiveVerdict(mailId: string, classified: RfqVerdict): RfqVerdict {
  const override = overrides.get(mailId)
  if (override === undefined) return classified
  return override === 'rfq' ? 'rfq' : 'not-rfq'
}

/** Whether the user has said this mail is a supplier's reply, whatever the classifier thinks. */
export function isMarkedSupplierReply(mailId: string): boolean {
  return overrides.get(mailId) === 'supplier-reply'
}

/** Whether the user has said this mail is NOT a supplier reply, overriding the detection. */
export function isMarkedNotReply(mailId: string): boolean {
  return overrides.get(mailId) === 'rfq'
}

/**
 * The ONE question both the badge and the "Create enquiry" action ask: does this mail answer
 * something we sent?
 *
 * The user's correction wins over the detection in both directions — marking a mail as an RFQ
 * un-suppresses the action on a mail our own document number appears in for an innocent reason,
 * which is the case tier 3 can plausibly get wrong.
 */
export function readsAsSupplierReply(mail: MailMessage, linked?: boolean): boolean {
  const override = overrides.get(mail.id)
  if (override === 'rfq') return false
  if (override === 'supplier-reply') return true
  return isSupplierReply(mail, linked)
}
